import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Paper,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useParams } from 'react-router-dom';
import { superadminService } from '../../../services/superadmin.service';
import { theme } from '../../../theme';

import SalonHero from '../components/SalonHero';
import SalonPhotos from '../components/SalonPhotos';
import SalonTeam from '../components/SalonTeam';
import SalonServices from '../components/SalonServices';
import SalonOverview from '../components/SalonOverview';
import SalonHistory from '../components/SalonHistory';
import SalonReviews from '../components/SalonReviews';
import SalonLocationRepairPanel from '../components/SalonLocationRepairPanel';
import SalonThemeEditor from '../../common/salonTheme/SalonThemeEditor';

// Placeholder components for sections not yet fully refactored

const SalonDetail = () => {
  const { salonId } = useParams();

  const [loading, setLoading] = useState(true);
  const [salon, setSalon] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrError, setQrError] = useState('');

  // Toast State
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ ...toast, open: false });
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await superadminService.retrieveSalon(salonId);
      setSalon(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [salonId]);

  const handleUpdateSalon = async (id, data) => {
    try {
      await superadminService.updateSalon(id, data);
      showToast('Modification mise à jour', 'success');
      await load();
    } catch (error) {
      console.error(error);
      const openingHoursHelpText = 'Horaires invalides: utilisez des heures différentes (ex: 09:00 -> 18:00). Pour 24h/24, utilisez 00:00 -> 00:00.';
      const rawErrorPayload = JSON.stringify(error?.response?.data || error || {}).toLowerCase();
      if (rawErrorPayload.includes("l'heure d'ouverture et de fermeture ne peuvent pas être identiques")) {
        showToast(openingHoursHelpText, 'error');
      } else {
        showToast('Erreur lors de la mise à jour.', 'error');
      }
      throw error;
    }
  };

  const handleUploadPhoto = async (id, formData) => {
    await superadminService.uploadSalonPhoto(id, formData);
    await load();
  };

  const handleDeletePhoto = async (id, photoId) => {
    await superadminService.deleteSalonPhoto(id, photoId);
    await load();
  };

  const handleSetPrimaryPhoto = async (photoId) => {
    try {
      await superadminService.setSalonPhotoPrimary(salon.salon_id, photoId);
      showToast('Photo principale mise à jour.', 'success');
      await load();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la mise à jour.', 'error');
    }
  };

  const handleReorderPhotos = async (orderedIds) => {
    try {
      await superadminService.reorderSalonPhotos(salon.salon_id, orderedIds);
      // Optimistic update could be done here, but we reload for simplicity and data consistency
      await load();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la réorganisation.', 'error');
    }
  };

  const toSlug = (value) => {
    if (!value) return 'salon';
    return value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 60) || 'salon';
  };

  const handleOpenQr = async () => {
    if (!salon?.salon_id) return;
    setQrDialogOpen(true);
    setQrError('');
    setQrLoading(true);
    try {
      const res = await superadminService.getSalonQr(salon.salon_id);
      setQrData(res.data);
    } catch (error) {
      console.error(error);
      setQrError('Impossible de générer le code QR.');
    } finally {
      setQrLoading(false);
    }
  };

  const handleCopyPublicUrl = async () => {
    try {
      if (!qrData?.public_url) return;
      await navigator.clipboard.writeText(qrData.public_url);
      showToast('Lien public copié.', 'success');
    } catch (error) {
      showToast('Impossible de copier le lien.', 'error');
    }
  };

  const qrDownloadFilename = `${toSlug(salon?.name)}_qr.png`;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  if (!salon) {
    return <Typography>Salon introuvable.</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 10 }}>
      <SalonHero salon={salon} onUpdate={handleUpdateSalon} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<QrCode2Icon />}
          onClick={handleOpenQr}
          sx={{
            borderRadius: theme.borderRadius.lg,
            fontWeight: 800,
            textTransform: 'none'
          }}
        >
          Voir le code QR
        </Button>
      </Box>

      <SalonLocationRepairPanel
        salon={salon}
        onUpdated={(updatedSalon) => {
          if (updatedSalon) {
            setSalon((current) => ({ ...current, ...updatedSalon }));
          } else {
            load();
          }
        }}
        onOpenHistory={() => setTabValue(6)}
      />

      <Paper
        sx={{
          borderRadius: theme.borderRadius.xl,
          border: `1px solid ${theme.colors.border.light}`,
          overflow: 'hidden',
          boxShadow: theme.boxShadow.sm,
          minHeight: 500
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 1, sm: 2 }, bgcolor: theme.colors.background.secondary }}>
          {/* Mobile Select Menu */}
          <Box sx={{ display: { xs: 'block', sm: 'none' }, py: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="salon-section-label">Section</InputLabel>
              <Select
                labelId="salon-section-label"
                value={tabValue}
                label="Section"
                onChange={(e) => setTabValue(e.target.value)}
                sx={{
                  fontWeight: 700,
                  bgcolor: 'white',
                  borderRadius: theme.borderRadius.md
                }}
              >
                <MenuItem value={0}>Vue d'ensemble</MenuItem>
                <MenuItem value={1}>Photos</MenuItem>
                <MenuItem value={2}>Équipe</MenuItem>
                <MenuItem value={3}>Prestations</MenuItem>
                <MenuItem value={4}>Avis</MenuItem>
                <MenuItem value={5}>Apparence</MenuItem>
                <MenuItem value={6}>Historique</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Desktop/Tablet Tabs */}
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              display: { xs: 'none', sm: 'flex' },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                minHeight: 56,
                minWidth: { xs: 100, sm: 160 }
              }
            }}
          >
            <Tab label="Vue d'ensemble" />
            <Tab label="Photos" />
            <Tab label="Équipe" />
            <Tab label="Prestations" />
            <Tab label="Avis" />
            <Tab label="Apparence" />
            <Tab label="Historique" />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 4 } }}>
          {tabValue === 0 && <SalonOverview salon={salon} onUpdate={handleUpdateSalon} />}
          {tabValue === 1 && (
            <SalonPhotos
              salon={salon}
              onUpload={handleUploadPhoto}
              onDelete={handleDeletePhoto}
              onSetPrimary={handleSetPrimaryPhoto}
              onReorder={handleReorderPhotos}
            />
          )}
          {tabValue === 2 && <SalonTeam employees={salon.employees} salonId={salon.salon_id} />}
          {tabValue === 3 && <SalonServices salonId={salon.salon_id} />}
          {tabValue === 4 && <SalonReviews salonId={salon.salon_id} />}
          {tabValue === 5 && <SalonThemeEditor salonId={salon.salon_id} salonName={salon.name} />}
          {tabValue === 6 && <SalonHistory salonId={salon.salon_id} />}
        </Box>
      </Paper>
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', fontWeight: 600, borderRadius: theme.borderRadius.md }} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Code QR du salon</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {qrLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : qrError ? (
            <Alert severity="error">{qrError}</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              {qrData?.qr_code_data_uri && (
                <Box
                  sx={{
                    width: 260,
                    height: 260,
                    borderRadius: 2,
                    border: `1px solid ${theme.colors.border.light}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    overflow: 'hidden'
                  }}
                >
                  <img
                    src={qrData.qr_code_data_uri}
                    alt="QR code"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </Box>
              )}

              <TextField
                value={qrData?.public_url || ''}
                label="Lien public"
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />

              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyPublicUrl}
                  disabled={!qrData?.public_url}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Copier
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<DownloadIcon />}
                  component="a"
                  href={qrData?.qr_code_data_uri || '#'}
                  download={qrDownloadFilename}
                  disabled={!qrData?.qr_code_data_uri}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Télécharger
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setQrDialogOpen(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalonDetail;
